/**
 * IR tree → `@webflow/XscpData` payload.
 *
 * Nodes are emitted as a FLAT array with id references rather than a nested structure, which
 * is how the format works. Children are pushed before their parents (post-order) purely for
 * determinism — array order does not appear to be semantic to Webflow's parser.
 */

import type { IRElement, IRNode } from '@lp/shared';
import { makeId } from './ids.js';
import { StyleRegistry } from './styleRegistry.js';
import type { XscpElementNode, XscpNode, XscpNodeData, XscpPayload } from './types.js';

export interface EmitOptions {
  /** Prefix for id seeds, so two sections never collide. Usually the block id and index. */
  seedPrefix?: string;
  registry?: StyleRegistry;
}

export class UnsupportedNodeError extends Error {
  constructor(kind: string) {
    super(
      `Cannot emit IR node of kind "${kind}". Slots must be expanded before export — call ` +
        `expandSlots() first.`,
    );
    this.name = 'UnsupportedNodeError';
  }
}

/**
 * Does this HTML contain a script?
 *
 * Drives `data.embed.meta.script`, which is how Webflow represents `w-embed w-script` — the
 * script variant is a data flag derived from the content, not a class in `classes[]`.
 */
function containsScript(html: string): boolean {
  return /<script[\s>]/i.test(html);
}

/**
 * The `data` block for an Image, matching real captured payloads.
 *
 * `attr.src` is a plain absolute URL; the Webflow asset id, if there is one, goes separately
 * in `img.id`. Width and height are STRINGS — either a numeric string or the literal `"auto"`.
 *
 * `alt` has two sentinel values Webflow understands: `__wf_reserved_inherit` (take the alt
 * from the Assets panel) and `__wf_reserved_decorative` (render `alt=""`). We emit the latter
 * for an empty alt so the intent is explicit rather than accidental.
 */
function imageData(node: IRElement): XscpNodeData {
  const attr = node.attr ?? {};
  const alt = attr['alt'];

  return {
    attr: {
      src: attr['src'] ?? '',
      alt: alt && alt.trim() ? alt : '__wf_reserved_decorative',
      loading: attr['loading'] ?? 'lazy',
      width: attr['width'] ?? 'auto',
      height: attr['height'] ?? 'auto',
      id: attr['id'] ?? '',
      ...(attr['fetchpriority'] ? { fetchpriority: attr['fetchpriority'] } : {}),
    },
    // Images we place are hosted elsewhere (Unsplash, ImageKit) rather than uploaded to the
    // destination site's Assets panel, so there is no asset id to reference.
    img: node.attr?.['data-wf-asset-id'] ? { id: node.attr['data-wf-asset-id'] } : {},
    xattr: node.xattr ?? [],
    search: { exclude: false },
    visibility: { conditions: [] },
    displayName: node.displayName ?? '',
  };
}

/** The `data.embed` block, matching the shape of real captured payloads. */
function embedData(html: string): XscpNodeData {
  return {
    embed: {
      type: 'html',
      meta: {
        // Must be byte-identical to the node's `v`.
        html,
        div: /<div[\s>]/i.test(html),
        iframe: /<iframe[\s>]/i.test(html),
        script: containsScript(html),
        compilable: false,
      },
    },
    insideRTE: false,
    attr: { id: '' },
    xattr: [],
    search: { exclude: false },
    visibility: { conditions: [] },
    displayName: '',
  };
}

export function emitXscp(nodes: IRNode[], opts: EmitOptions = {}): XscpPayload {
  const styles = opts.registry ?? new StyleRegistry();
  const out: XscpNode[] = [];
  const prefix = opts.seedPrefix ?? 'root';

  function walk(node: IRNode, path: string): string {
    switch (node.kind) {
      case 'text': {
        const id = makeId(path);
        // A text node is a bare leaf: no type, no tag, no classes.
        out.push({ _id: id, text: true, v: node.value });
        return id;
      }

      case 'el': {
        const id = makeId(path);
        const classes = node.cls.map((c) => styles.ensure(c.name));
        // Children first, so the flat array is post-order and stable.
        const children = node.children.map((child, i) => walk(child, `${path}/${i}`));
        out.push(elementNode(id, node, classes, children));
        return id;
      }

      case 'rich': {
        const id = makeId(path);
        const classes = node.cls.map((c) => styles.ensure(c.name));
        out.push({
          _id: id,
          type: 'RichText',
          tag: 'div',
          classes,
          children: [],
          data: { tag: 'div', attr: {}, xattr: [], search: { exclude: false } },
          v: node.html,
        });
        return id;
      }

      case 'embed': {
        const id = makeId(path);
        const classes = node.cls.map((c) => styles.ensure(c.name));
        // The HTML lives at the top-level `v`, mirrored inside `data.embed.meta.html`.
        // `w-embed` / `w-embed w-script` are never in `classes[]` — they are implicit.
        out.push({
          _id: id,
          type: 'HtmlEmbed',
          tag: 'div',
          classes,
          children: [],
          data: embedData(node.html),
          v: node.html,
        });
        return id;
      }

      case 'slot':
        throw new UnsupportedNodeError('slot');
    }
  }

  nodes.forEach((node, i) => walk(node, `${prefix}/${i}`));

  return {
    nodes: out,
    styles: styles.finalise(),
    assets: [],
    ix1: [],
    ix2: { interactions: [], events: [], actionLists: [] },
  };
}

function elementNode(
  id: string,
  node: IRElement,
  classes: string[],
  children: string[],
): XscpElementNode {
  // Image nodes have their own `data` shape in real payloads: no `tag`, no `devlink`, a
  // plain `visibility`, and an `img` object carrying the asset id separately from `src`.
  if (node.wf === 'Image') {
    return { _id: id, type: 'Image', tag: 'img', classes, children: [], data: imageData(node) };
  }

  const data: XscpNodeData = {
    tag: node.tag,
    attr: node.attr ?? {},
    xattr: node.xattr ?? [],
    search: { exclude: false },
    visibility: { conditions: [], keepInHtml: { tag: 'False', val: {} } },
    devlink: { runtimeProps: {}, slot: '' },
  };

  if (node.displayName) data.displayName = node.displayName;
  if (node.wf === 'Section') data.grid = { type: 'section' };

  // Webflow stores a link's destination in `data.link`, not only in the href attribute.
  if (node.wf === 'Link' && node.attr?.['href']) {
    const href = node.attr['href'];
    data.link = href.startsWith('mailto:')
      ? { email: href.slice(7) }
      : { url: href, target: node.attr['target'] ?? '' };
  }

  return { _id: id, type: node.wf, tag: node.tag, classes, children, data };
}
